<template>
  <div class="overview">
    <v-row class="ma-0">
      <v-col class="pa-0">
        <PageHeader :title="$t('tolling.header')" />
      </v-col>
    </v-row>
    <v-row class="variant-label">{{ $t('map.tolldomains.overview.variant_select_label') }}</v-row>
    <v-row class="ma-0">
      <v-col class="pa-0 d-flex">
        <v-select
          v-if="variantSelected && variantSelected.name"
          id="variant-list"
          v-model="variantSelected"
          attach
          :items="getVariants"
          :item-text="variant => variant.name"
          :item-value="variant => variant"
          dark
          outlined
          dense
          class="variant-select"
          @change="changeVariant()"
        />
        <v-btn
          v-if="getCurrentUserIsAbleTo('write', 'geometryManagement') && !loading"
          id="overview-add-configuration"
          class="action-button rounded-btn ml-5"
          outlined
          rounded
          @click="addConfiguration()"
        >
          <v-icon>
            mdi-plus
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="loading" class="mx-3">
      <v-col>
        <v-progress-circular
          indeterminate
          color="amber"
        />
      </v-col>
    </v-row>
    <v-col v-if="!loading && noPeriod" id="overview-no-period" class="period-main-container">
      <v-row no-gutters>
        <v-row class="period-list-container ma-0">
          <v-col class="pa-0">
            <div class="periods-header no-zone">
              <span v-if="variantSelected && variantSelected.name" id="overview-period-list-title">{{ $t('map.tolldomains.overview.period_list_title', { variant: variantSelected.name }) }}</span>
              <span v-else id="overview-period-list-title">{{ $t('map.tolldomains.overview.period_list_title', { variant: 'Default' }) }}</span>
              <v-btn
                v-if="getCurrentUserIsAbleTo('write', 'geometryManagement') && !loading"
                id="overview-add-period"
                class="action-button mx-5"
                dark
                rounded
                outlined
                @click="addNewPeriod()"
              >
                {{ $t('map.tolldomains.actions.update_map') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-row>
      <v-row class="ma-0">
        <v-col class="pa-0">
          <div class="map-container no-zone">
            <img
              id="map-placeholder"
              :src="require('@/assets/images/overview-world.svg')"
              alt="overview-world"
            >
          </div>
        </v-col>
        <v-col class="col-5 pa-0">
          <div class="info-panel">
            <div class="info-panel-title">{{ $t('map.tolldomains.zone_list') }}</div>
            <DomainsListContent
              v-if="displayedPeriod && displayedPeriod.zones"
              :selectedZones="displayedPeriod.zones"
              :variant="variantSelected.id"
            />
          </div>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-if="!loading && !noPeriod" class="period-main-container">
      <v-row class="period-list-container ma-0">
        <v-col class="pa-0">
          <div class="periods-header">
            <span class="chip" :class="displayedPeriodState">{{ displayedPeriodState }}</span>
            <span v-if="variantSelected && variantSelected.name" id="overview-period-list-title">{{ $t('map.tolldomains.overview.period_list_title', { variant: variantSelected.name }) }}</span>
            <span v-else id="overview-period-list-title">{{ $t('map.tolldomains.overview.period_list_title', { variant: 'Default' }) }}</span>
            <v-btn
              v-if="getCurrentUserIsAbleTo('write', 'geometryManagement') && !loading"
              id="overview-add-period"
              class="action-button mx-5"
              dark
              rounded
              outlined
              @click="addNewPeriod()"
            >
              {{ $t('map.tolldomains.actions.update_map') }}
            </v-btn>
          </div>
          <div v-if="!loading" class="period-list">
            <v-btn
              id="overview-previous-period"
              class="paging"
              :disabled="selectedPeriodIndex <= 0"
              icon
              @click="previousPeriod"
            >
              <v-icon size="34">mdi-chevron-left-circle</v-icon>
            </v-btn>

            <PeriodListItem
              :periodState="displayedPeriodState"
              :period="allPeriods[selectedPeriodIndex]"
            />
            <span class="next-page">
              <v-btn
                id="overview-next-period"
                class="paging"
                :disabled="selectedPeriodIndex >= allPeriods.length - 1"
                icon
                @click="nextPeriod"
              >
                <v-icon size="34">mdi-chevron-right-circle</v-icon>
              </v-btn>
            </span>
          </div>
          <div class="page-dots">
            <div>
              <span
                v-for="(period, index) in allPeriods"
                :id="`overview-period-${period.id}`"
                :key="`period-item-${index}`"
                class="dot"
                :class="index === selectedPeriodIndex ? 'active' : ''"
                @click="navigateToPeriod(index)"
              />
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="ma-0">
        <v-col class="pa-0">
          <div v-if="!loading" class="map-container">
            <l-map
              ref="period-map"
              :options="{ preferCanvas: true, minZoom: 3 }"
              @ready="onMapReady"
            >
              <l-tile-layer v-bind="tilesOpts" />
            </l-map>
          </div>
        </v-col>
        <v-col class="col-5 pa-0">
          <div v-if="!loading" class="info-panel">
            <div class="info-panel-title">{{ $t('map.tolldomains.zone_list') }}</div>
            <DomainsListContent
              v-if="displayedPeriod && displayedPeriod.zones"
              :selectedZones="displayedPeriod.zones"
              :variant="variantSelected.id"
            />
          </div>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { Period, Periods, Zone } from 'ctp-geometry';
import { store as tollDomainStore } from '@/store/modules/tollDomain/types';
import { store as userStore } from '@/store/modules/user/types';
import { store as permissionsStore } from '@/store/modules/permissions/types';
import L from 'leaflet';
import { LMap, LTileLayer } from 'vue2-leaflet';
import { MAP_ATTRIBUTION, TILES_URL } from '@/constants/map/mapConstants';
import { GeoJsonObject } from 'geojson';
import Utils from '@/mixins/Utils';
import PageHeader from '@/components/common/PageHeader/PageHeader.vue';
import { Variant } from 'ctp-toll-domain';
import PeriodListItem from './components/PeriodListItem.vue';
import ZonePanel from './components/ZonePanel.vue';
import DomainsListContent from '../DomainsListContent/DomainsListContent.vue';

@Component({
  components: {
    PageHeader,
    DomainsListContent,
    PeriodListItem,
    ZonePanel,
    LMap,
    LTileLayer
  }
})
export default class Overview extends Mixins(Utils) {
 @tollDomainStore.Action
  private fetchPeriods!: (payload: { masterId: number, variantId?: number }) => Promise<Periods>;

  @tollDomainStore.Action
  private fetchVariants: () => Promise<void>;

  @tollDomainStore.Getter
  private getVariants: Variant[];

  @userStore.Getter
  private getSelectedMasterAccount: MasterAccountInfo;

  @permissionsStore.Getter
  private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

  private periods: Periods = {
    past: [],
    current: {},
    future: {}
  };

  private allPeriods: Period[] = [];

  private variantSelected: { id?: number, name?: string } = {};

  private selectedPeriodIndex = 0;

  private layers: L.Layer[] = [];

  private geoGroup: L.FeatureGroup;

  private displayedPeriod: Period = {};

  private displayedPeriodState = 'current';

  private loading = true;

  private noPeriod = false;

  private map: L.Map;

  private tilesOpts = {
    url: TILES_URL,
    attribution: MAP_ATTRIBUTION
  };

  /**
   * Map init function
   * Only executed when API getPeriods has been called
   * Inits the map with current geo zones (string from API parsed to geoJSON object)
   * A featureGroup is created for easy getBound/Focus when changing geometries
   */
  private onMapReady() {
    if (!this.loading && this.displayedPeriod && this.displayedPeriod.zones) {
      const mapComponent = this.$refs['period-map'] as LMap;
      mapComponent.mapObject.invalidateSize();
      this.map = mapComponent.mapObject;
      if (this.displayedPeriod.zones) {
        this.geoGroup = L.featureGroup();
        this.displayedPeriod.zones!.forEach((zone: Zone) => {
          const geo = JSON.parse(zone.geo!);
          this.addLayer(geo, zone.code, 'current');
        });
        this.map.fitBounds(this.geoGroup.getBounds());
      }
    }
  }

  /**
   * Add a new period (button update map)
   * Redirects to the stepper with geozip upload
   */
  private addNewPeriod() {
    const variant = this.variantSelected.id ? this.variantSelected.id : 0;
    this.$router.push({
      name: 'tolldomains-overview-new-period-stepper',
      params: {
        selectedVariant: variant.toString()
      }
    });
  }

  /**
   * Add a new variant (button add configuration)
   * Redirects to the stepper, passing boolean
   */
  private addConfiguration() {
    this.$router.push({
      name: 'tolldomains-overview-new-period-stepper',
      params: {
        newVariant: 'true'
      }
    });
  }

  /**
   * Adds a layer from a geoJSON object
   * Adds the layer to the featureGroup
   * this.layers will be updated when clicking on a period
   * State is used for coloring
   */
  private addLayer(geo: GeoJsonObject, code: string | undefined, state: string) {
    const colors = {
      current: '#ffa500',
      past: '#7079df',
      future: '#44ae88'
    };
    const zone = L.geoJSON(geo, { onEachFeature(feature, layer) {
      layer.bindTooltip(`${code}`, { permanent: true });
    },
    style: { color: colors[state as keyof typeof colors], weight: 2, opacity: 1 } }).addTo(this.map);
    this.layers.push(zone);
    this.geoGroup.addLayer(zone);
  }

  /**
   * Actions on master selection
   * Gets a list of master accounts synchronously. Necessary step to call the API
   * Gets the list of periods for the current master account
   * Displays the current geo zones by default if it exists or a future for new rules
   * this.displayedPeriod will be updated when clicking on an arrow
   * We track the selected period with an index (selectedPeriodIndex), current or future is pre-selected
   */
  @Watch('getSelectedMasterAccount', { immediate: true, deep: true })
  private async onMasterChange() {
    const account = this.getSelectedMasterAccount;
    if (account && account.name) {
      this.loading = true;
      await this.fetchVariants();
      this.variantSelected = this.getVariants[0];
      this.periods = await this.fetchPeriods({ masterId: account.id });
      this.updateMap();
      this.loading = false;
    }
  }

  /**
   * Get the right geometry for the choosen variant in the top dropdown
   */
  private async changeVariant() {
    const account = this.getSelectedMasterAccount;
    if (account.name) {
      this.loading = true;
      this.periods = await this.fetchPeriods({ masterId: account.id, variantId: this.variantSelected.id });
      this.updateMap();
      this.loading = false;
    }
  }

  private updateMap() {
    if (!this.periods.current && !this.periods.future && !this.periods.past) {
      this.noPeriod = true;
    } else {
      this.noPeriod = false;
      this.allPeriods = [...this.periods.past?.reverse() || [], this.periods.current, this.periods.future].filter(n => n);
      if (this.periods.current) {
        this.displayedPeriod = this.periods.current;
        this.displayedPeriodState = 'current';
        this.selectedPeriodIndex = this.allPeriods.findIndex(p => p.id === this.periods.current.id);
      } else {
        this.displayedPeriod = this.periods.future;
        this.displayedPeriodState = 'future';
        this.selectedPeriodIndex = this.allPeriods.findIndex(p => p.id === this.periods.future.id);
      }
    }
  }

  /**
   * Updates the displayed zones and selected zone on click
   * Layers array and featureGroup are reinitialised
   */
  private updatePeriod(period: Period) {
    const state = this.getPeriodState(this.allPeriods[this.selectedPeriodIndex]);
    this.layers.forEach((layer: L.Layer) => {
      this.map.removeLayer(layer);
    });
    this.geoGroup.clearLayers();
    this.displayedPeriod = period;
    this.displayedPeriodState = state;
    this.layers = [];
    this.displayedPeriod.zones!.forEach((zone: Zone) => {
      const geo = JSON.parse(zone.geo!);
      this.addLayer(geo, zone.code, state);
    });
    this.map.fitBounds(this.geoGroup.getBounds());
  }

  private previousPeriod() {
    this.selectedPeriodIndex--;
    this.updatePeriod(this.allPeriods[this.selectedPeriodIndex]);
  }

  private nextPeriod() {
    this.selectedPeriodIndex++;
    this.updatePeriod(this.allPeriods[this.selectedPeriodIndex]);
  }

  private navigateToPeriod(periodIndex: number) {
    this.selectedPeriodIndex = periodIndex;
    this.updatePeriod(this.allPeriods[this.selectedPeriodIndex]);
  }

  private getPeriodState(period: Period): string {
    if (this.periods.current && period.id === this.periods.current.id) {
      return 'current';
    }
    if (this.periods.future && period.id === this.periods.future.id) {
      return 'future';
    }
    return 'past';
  }
}
</script>
<style lang="scss" scoped>
@import './styles/overview.scss';
</style>
